import { useEffect, useMemo, useState } from 'react';
import { Check, Clock, Loader2, X } from 'lucide-react';
import {
  combineLocal,
  formatScheduleDate,
  formatScheduleTime,
  isSameLocalDay,
  localClock,
  localDateKey,
  nextMinuteClock,
} from '../../lib/postSchedule';

interface ScheduledTimeEditorProps {
  /** ISO timestamp the post is currently scheduled for. */
  scheduledFor: string;
  disabled?: boolean;
  /** Persist the new timestamp. Throw to keep the editor open with an error. */
  onSave: (nextIso: string) => Promise<void>;
  /** Trailing text shown after the time (e.g. "AI post"). */
  suffix?: string;
  className?: string;
}

/**
 * Inline "date · time" line where only the time is editable.
 * Keeps the post on its current day; refuses times that have already passed
 * and caps the day at 23:59.
 */
export default function ScheduledTimeEditor({
  scheduledFor,
  disabled = false,
  onSave,
  suffix,
  className = '',
}: ScheduledTimeEditorProps) {
  const scheduled = useMemo(() => new Date(scheduledFor), [scheduledFor]);
  const valid = !Number.isNaN(scheduled.getTime());
  const [editing, setEditing] = useState(false);
  const [clock, setClock] = useState(() => (valid ? localClock(scheduled) : '12:00'));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!valid) return;
    setClock(localClock(scheduled));
    setEditing(false);
    setError(null);
  }, [scheduled, valid]);

  if (!valid) {
    return (
      <p className={`text-[12px] text-gray-500 ${className}`}>
        Not scheduled yet{suffix ? ` · ${suffix}` : ''}
      </p>
    );
  }

  const dateKey = localDateKey(scheduled);
  const now = new Date();
  const isToday = isSameLocalDay(scheduled, now);
  // After 11:59 PM there is no future minute left on this day.
  const todayAlreadyOver = isToday && now.getHours() === 23 && now.getMinutes() >= 59;
  const minClock = todayAlreadyOver ? '23:59' : isToday ? nextMinuteClock(now) : '00:00';

  const validate = (value: string): string | null => {
    const next = combineLocal(dateKey, value);
    if (!next) return 'Pick a time';
    if (next.getTime() <= Date.now()) return 'That time has already passed';
    return null;
  };

  const submit = async () => {
    const problem = validate(clock);
    if (problem) {
      setError(problem);
      return;
    }
    const next = combineLocal(dateKey, clock);
    if (!next) return;
    if (next.getTime() === scheduled.getTime()) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(next.toISOString());
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update time');
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <p className={`text-[12px] text-gray-500 flex flex-wrap items-center gap-x-1 ${className}`}>
        <span>{formatScheduleDate(scheduled)},</span>
        <button
          type="button"
          disabled={disabled || todayAlreadyOver}
          onClick={() => {
            setClock(localClock(scheduled));
            setError(null);
            setEditing(true);
          }}
          className="inline-flex items-center gap-1 rounded-md px-1 -mx-1 font-semibold text-gray-800 underline decoration-dotted underline-offset-2 hover:bg-gray-100 disabled:opacity-60 disabled:no-underline"
          title="Change the time"
        >
          <Clock className="h-3 w-3 text-gray-400" />
          {formatScheduleTime(scheduled)}
        </button>
        {suffix ? <span>· {suffix}</span> : null}
      </p>
    );
  }

  return (
    <div className={`text-[12px] text-gray-500 ${className}`}>
      <div className="flex flex-wrap items-center gap-1.5">
        <span>{formatScheduleDate(scheduled)},</span>
        <input
          type="time"
          value={clock}
          min={minClock}
          max="23:59"
          step={60}
          autoFocus
          disabled={saving}
          onChange={(e) => {
            setClock(e.target.value);
            setError(e.target.value ? validate(e.target.value) : null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void submit();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              setEditing(false);
              setError(null);
            }
          }}
          className="h-7 rounded-md border border-gray-300 px-1.5 text-[12px] font-semibold text-gray-900 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none disabled:opacity-60"
          aria-label="Scheduled time"
        />
        <button
          type="button"
          onClick={() => void submit()}
          disabled={saving || !!validate(clock)}
          className="inline-flex h-7 items-center gap-1 rounded-md bg-gray-900 px-2 text-[11px] font-semibold text-white hover:bg-gray-800 disabled:opacity-50"
        >
          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
          Save
        </button>
        <button
          type="button"
          onClick={() => {
            setEditing(false);
            setError(null);
          }}
          disabled={saving}
          className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-50"
          aria-label="Cancel"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      <p className={`mt-1 ${error ? 'text-red-600' : 'text-gray-400'}`}>
        {error
          ? error
          : isToday
            ? `Today · pick a time after ${formatScheduleTime(new Date())}`
            : 'Same day · any time up to 11:59 PM'}
      </p>
    </div>
  );
}
